/**
 * LiquiditaetDashboardWidget — Saldo-Widgets + Alerts für den Finanzen-Dashboard.
 *
 * Features (Issue #545):
 * - Aktueller Saldo pro Konto mit 7-Tage-Trend-Pfeil
 * - Konten gruppiert nach Geschäftseinheit (firma)
 * - Visueller Alarm bei Saldo < 1.000 €
 * - Tilgungsplan-Abweichungsalarm (Restschuld > Soll)
 * - Barkassen als „manuell — kein Sync" markiert
 */

import { useMemo, useEffect, useState } from 'react';
import {
  ArrowUpRight, ArrowDownRight, Minus, AlertTriangle, Ban,
  Banknote, Loader2, Building2,
} from 'lucide-react';
import {
  useKonten,
  useTransaktionen,
  FIRMEN,
  formatBetrag,
  type FinanzKonto,
} from '@/hooks/useLiquiditaet';
import { supabase } from '@/lib/supabase';
import {
  NIEDRIG_SALDO_SCHWELLE,
  istNiedrigerSaldo,
  istBarkasse,
  berechneTrend7Tage,
  gruppiereKontenNachFirma,
  hatTilgungsplanAbweichung,
  type FahrzeugTilgungsplanInfo,
} from '@/lib/liquiditaet-dashboard';

// ============================================================
// KLEINE HELFER-KOMPONENTEN
// ============================================================

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'neutral' }) {
  if (trend === 'up') return <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === 'down') return <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400" />;
}

function KontoKarte({
  konto,
  trend,
}: {
  konto: FinanzKonto;
  trend: 'up' | 'down' | 'neutral';
}) {
  const niedrig = istNiedrigerSaldo(konto.aktueller_saldo);
  const barkasse = istBarkasse(konto.typ);

  const borderClass = niedrig
    ? 'border-red-200 bg-red-50/40'
    : 'border-gray-100 bg-white';

  return (
    <div
      className={`rounded-lg border p-3 transition-shadow hover:shadow-sm ${borderClass}`}
      data-testid={`konto-karte-${konto.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-800 truncate">{konto.name}</span>
            {barkasse && (
              <span
                className="flex items-center gap-0.5 text-[9px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full shrink-0"
                data-testid="barkasse-badge"
              >
                <Ban className="w-2.5 h-2.5" />
                manuell — kein Sync
              </span>
            )}
          </div>
          {konto.iban && (
            <div className="text-[10px] text-gray-400 mt-0.5 font-mono truncate">{konto.iban}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className={`text-sm font-bold ${niedrig ? 'text-red-600' : 'text-gray-900'}`}>
            {formatBetrag(konto.aktueller_saldo)}
          </div>
          <div className="flex items-center justify-end gap-0.5 mt-0.5">
            <TrendIcon trend={trend} />
            <span className="text-[10px] text-gray-400">7 Tage</span>
          </div>
        </div>
      </div>

      {niedrig && (
        <div
          className="mt-2 flex items-center gap-1 text-[10px] font-medium text-red-600"
          data-testid="niedrig-saldo-alarm"
        >
          <AlertTriangle className="w-3 h-3" />
          Saldo unter {NIEDRIG_SALDO_SCHWELLE.toLocaleString('de-DE')} €
        </div>
      )}
    </div>
  );
}

function FirmaGruppe({
  firmaKey,
  konten,
  transaktionen,
}: {
  firmaKey: string;
  konten: FinanzKonto[];
  transaktionen: ReturnType<typeof useTransaktionen>['transaktionen'];
}) {
  const firmaInfo = FIRMEN[firmaKey];
  const label = firmaInfo?.name ?? firmaKey;
  const farbe = firmaInfo?.farbe ?? '#6B7280';

  const gesamtSaldo = konten
    .filter(k => k.ist_aktiv)
    .reduce((sum, k) => sum + Number(k.aktueller_saldo), 0);

  return (
    <div className="space-y-2" data-testid={`firma-gruppe-${firmaKey}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: farbe }}
          />
          <span className="text-xs font-semibold text-gray-700">{label}</span>
          <span className="text-[10px] text-gray-400">({konten.length})</span>
        </div>
        <span className="text-xs font-bold text-gray-800">
          {formatBetrag(gesamtSaldo)}
        </span>
      </div>
      <div className="space-y-1.5 pl-4">
        {konten.map(k => (
          <KontoKarte
            key={k.id}
            konto={k}
            trend={berechneTrend7Tage(transaktionen, k.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TilgungsplanAlerts({ fahrzeuge }: { fahrzeuge: FahrzeugTilgungsplanInfo[] }) {
  const heute = useMemo(() => new Date(), []);
  const abweichungen = useMemo(
    () => fahrzeuge.filter(fz => hatTilgungsplanAbweichung(fz, heute)),
    [fahrzeuge, heute],
  );

  if (abweichungen.length === 0) return null;

  return (
    <div
      className="mt-4 rounded-xl border border-orange-200 bg-orange-50/50 p-4"
      data-testid="tilgungsplan-alerts"
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-orange-600" />
        <h4 className="text-sm font-semibold text-orange-800">
          Tilgungsplan-Abweichung ({abweichungen.length})
        </h4>
      </div>
      <div className="space-y-2">
        {abweichungen.map(fz => (
          <div
            key={fz.id}
            className="flex items-center justify-between text-xs"
            data-testid={`tilgungsplan-alert-${fz.id}`}
          >
            <span className="text-orange-800 font-medium">
              {fz.kennzeichen ?? fz.id}
            </span>
            <span className="text-orange-700">
              Restschuld {formatBetrag(fz.restschuld ?? 0)} &gt; Soll
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// HOOK: Fahrzeuge mit Tilgungsplan laden
// ============================================================

function useFahrzeugeTilgungsplan() {
  const [fahrzeuge, setFahrzeuge] = useState<FahrzeugTilgungsplanInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('fahrzeuge')
          .select('id, kennzeichen, restschuld, monatliche_rate, laufzeitende, firma')
          .eq('status', 'aktiv')
          .not('monatliche_rate', 'is', null)
          .not('laufzeitende', 'is', null);
        if (!error && data) setFahrzeuge(data as FahrzeugTilgungsplanInfo[]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { fahrzeuge, loading };
}

// ============================================================
// HAUPT-WIDGET
// ============================================================

export default function LiquiditaetDashboardWidget() {
  const { konten, loading: kontenLoading } = useKonten();
  const { transaktionen, loading: txLoading } = useTransaktionen();
  const { fahrzeuge, loading: fzLoading } = useFahrzeugeTilgungsplan();

  const loading = kontenLoading || txLoading || fzLoading;

  const aktiveKonten = useMemo(() => konten.filter(k => k.ist_aktiv), [konten]);
  const gruppiertNachFirma = useMemo(
    () => gruppiereKontenNachFirma(aktiveKonten),
    [aktiveKonten],
  );

  const firmaReihenfolge = [
    'sv_buero_koop', 'lcs_gmbh', 'mietgo', 'unfallgiganten', 'privat', 'prolea_fzco',
  ];
  const sortierteFirmen = [
    ...firmaReihenfolge.filter(f => gruppiertNachFirma[f]),
    ...Object.keys(gruppiertNachFirma).filter(f => !firmaReihenfolge.includes(f)),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (aktiveKonten.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 text-sm">
        Keine aktiven Konten vorhanden
      </div>
    );
  }

  return (
    <div data-testid="liquiditaet-dashboard-widget">
      <div className="space-y-5">
        {sortierteFirmen.map(firmaKey => (
          <FirmaGruppe
            key={firmaKey}
            firmaKey={firmaKey}
            konten={gruppiertNachFirma[firmaKey]}
            transaktionen={transaktionen}
          />
        ))}
      </div>
      <TilgungsplanAlerts fahrzeuge={fahrzeuge} />
    </div>
  );
}
